import { t } from '@hefe/i18n';
import {
  Club,
  ClubSettings,
  RequestBasePostUserCustomAttribute,
  RequestBaseUpdateClubWelcomeText,
  ResponseBaseGetClub,
  ResponseBaseGetClubTasks,
  ResponseBaseGetDataProcessingAgreement,
  ResponseBaseGetUserCustomAttributes,
  ResponseBasePostClubTask,
  ResponseBasePostUserCustomAttribute,
  ResponseBaseUpdateClubWelcomeText,
  ResponseBaseUpdateUserCustomAttribute,
  SelectOption,
  Task,
  UserCustomAttribute,
  UserCustomAttributeType,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { setBasketballSeasonPlanerVisibleTasks } from '../../basketball/actions/basketball-game/basketball-game.actions';
import {
  createClubTask,
  deleteTask,
  fetchClubTasks,
  updateTask,
} from '../tasks/tasks.actions';

const FEATURE_KEY = 'club';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
const fetchClub = createAsyncThunk('club/get', async (thunkAPI) => {
  const response = await API.club().clubdata();
  return response?.data;
});

const updateClubWelcomeText = createAsyncThunk(
  'club/updateWelcomeText',
  async (data: RequestBaseUpdateClubWelcomeText, thunkAPI) => {
    const response = await API.club().updateWelcomeText(data);
    return response?.data;
  }
);

const updateClubSettings = createAsyncThunk(
  'club/updateSettings',
  async (data: ClubSettings, thunkAPI) => {
    const response = await API.club().updateSettings({
      settings: data,
    });
    return response?.data;
  }
);

const uploadClubAvatar = createAsyncThunk(
  'club/uploadAvatar',
  async (data: any, thunkAPI) => {
    const response = await API.club().uploadLogo(data);
    return response?.data;
  }
);

export const deleteClubAvatar = createAsyncThunk(
  'club/deleteAvatar',
  async (thunkAPI) => {
    const response = await API.club().deleteLogo();
    return response?.data;
  }
);

const fetchDataProcessingAgreement = createAsyncThunk(
  'club/fetchDataProcessingAgreement',
  async (thunkAPI) => {
    const response = await API.club().getDataProcessingAgreement();
    return response?.data;
  }
);

const agreeToDataProcessingAgreement = createAsyncThunk(
  'club/agreeToDataProcessingAgreement',
  async (thunkAPI) => {
    const response = await API.club().agreeToDataProcessingAgreement();
    return response?.data;
  }
);

/*********************************************************
 *                    Custom user attributes
 *********************************************************/
const fetchClubCustomUserAttributes = createAsyncThunk(
  'club/getCustomUserAttributes',
  async (thunkAPI) => {
    const response = await API.clubCustomUserAttributes().getAll();
    return response?.data;
  }
);

const createClubCustomUserAttribute = createAsyncThunk(
  'club/createCustomUserAttribute',
  async (data: RequestBasePostUserCustomAttribute, thunkAPI) => {
    const response = await API.clubCustomUserAttributes().create(data);
    return response?.data;
  }
);

const updateClubCustomUserAttribute = createAsyncThunk(
  'club/updateCustomUserAttribute',
  async (data: RequestBasePostUserCustomAttribute, thunkAPI) => {
    const response = await API.clubCustomUserAttributes().update(data);
    return response?.data;
  }
);

const deleteClubCustomUserAttribute = createAsyncThunk<
  any,
  UserCustomAttribute
>(
  'club/deleteCustomUserAttribute',
  async (data: UserCustomAttribute, thunkAPI) => {
    const response = await API.clubCustomUserAttributes().delete(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface ClubState {
  club: Club;
  customUserAttributes: UserCustomAttribute[];
  tasks: Task[];
  settings: ClubSettings;
  dataProcessingAgreement: ResponseBaseGetDataProcessingAgreement;
}

const initialState: ClubState = {
  club: {
    id: 0,
    city: '',
    country: '',
    name: '',
    street: '',
    zipcode: 0,
  },
  customUserAttributes: [],
  tasks: [],
  settings: {
    basketball: {
      functions: {
        car_reservations: true,
        coach_presence: true,
      },
      season_planer: {
        visible_tasks: [],
      },
    },
    general: {
      functions: {
        partners: true,
      },
    },
  },
  dataProcessingAgreement: {
    latest_data: '',
    latest_version: 0,
    all_accepted_versions: [],
  },
};

const ClubSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchClub.fulfilled,
      (state: ClubState, action: PayloadAction<ResponseBaseGetClub>) => {
        state.club = action.payload.data;
      }
    );
    builder.addCase(
      updateClubWelcomeText.fulfilled,
      (
        state: ClubState,
        action: PayloadAction<ResponseBaseUpdateClubWelcomeText>
      ) => {
        if (action.payload) {
          state.club = {
            ...state.club,
            ...action.payload,
          };
        }
      }
    );
    builder.addCase(
      updateClubSettings.fulfilled,
      (state: ClubState, action: PayloadAction<Club>) => {
        if (action.payload) {
          state.club = {
            ...state.club,
            ...action.payload,
          };
        }
      }
    );
    builder.addCase(
      uploadClubAvatar.fulfilled,
      (state: ClubState, action: PayloadAction<ResponseBaseGetClub>) => {
        state.club = action.payload.data;
      }
    );
    builder.addCase(
      deleteClubAvatar.fulfilled,
      (state: ClubState, action: PayloadAction<ResponseBaseGetClub>) => {
        state.club = action.payload.data;
      }
    );
    builder.addCase(
      fetchDataProcessingAgreement.fulfilled,
      (
        state: ClubState,
        action: PayloadAction<ResponseBaseGetDataProcessingAgreement>
      ) => {
        state.dataProcessingAgreement = action.payload;
      }
    );
    builder.addCase(
      agreeToDataProcessingAgreement.fulfilled,
      (
        state: ClubState,
        action: PayloadAction<ResponseBaseGetDataProcessingAgreement>
      ) => {
        state.dataProcessingAgreement = action.payload;
      }
    );
    /*********************************************************
     *                    Custom user attributes
     *********************************************************/
    builder.addCase(
      fetchClubCustomUserAttributes.fulfilled,
      (
        state: ClubState,
        action: PayloadAction<ResponseBaseGetUserCustomAttributes>
      ) => {
        state.customUserAttributes = action.payload;
      }
    );
    builder.addCase(
      createClubCustomUserAttribute.fulfilled,
      (
        state: ClubState,
        action: PayloadAction<ResponseBasePostUserCustomAttribute>
      ) => {
        state.customUserAttributes.push(action.payload);
        state.customUserAttributes.sort(
          (a: UserCustomAttribute, b: UserCustomAttribute) =>
            a.name.localeCompare(b.name)
        );
      }
    );
    builder.addCase(
      updateClubCustomUserAttribute.fulfilled,
      (
        state: ClubState,
        action: PayloadAction<ResponseBaseUpdateUserCustomAttribute>
      ) => {
        state.customUserAttributes = state.customUserAttributes.map((att) =>
          att.id === action.payload.id ? action.payload : att
        );
      }
    );
    builder.addCase(
      deleteClubCustomUserAttribute.fulfilled,
      (state: ClubState, action: PayloadAction<UserCustomAttribute>) => {
        state.customUserAttributes = state.customUserAttributes.filter(
          (att) => att.id !== action.payload.id
        );
      }
    );
    /*********************************************************
     *                    Tasks
     *********************************************************/
    builder.addCase(
      fetchClubTasks.fulfilled,
      (state: ClubState, action: PayloadAction<ResponseBaseGetClubTasks>) => {
        state.tasks = action.payload;
      }
    );
    builder.addCase(
      createClubTask.fulfilled,
      (state: ClubState, action: PayloadAction<ResponseBasePostClubTask>) => {
        state.tasks.push(action.payload);
        state.tasks = state.tasks.sort((a: Task, b: Task) =>
          a.name.localeCompare(b.name)
        );
      }
    );
    builder.addCase(
      updateTask.fulfilled,
      (state: ClubState, action: PayloadAction<Task>) => {
        if (action.payload) {
          state.tasks = state.tasks.map((task) =>
            task.id === action.payload.id ? action.payload : task
          );
        }
      }
    );
    builder.addCase(
      deleteTask.fulfilled,
      (state: ClubState, action: PayloadAction<Task>) => {
        if (action.payload) {
          state.tasks = state.tasks.filter(
            (task) => task.id !== action.payload.id
          );
        }
      }
    );
    /*********************************************************
     *                    Basketball Stuff
     *********************************************************/
    builder.addCase(
      setBasketballSeasonPlanerVisibleTasks.fulfilled,
      (state: ClubState, action: PayloadAction<number[]>) => {
        if (action.payload) {
          state.settings.basketball.season_planer.visible_tasks =
            action.payload;
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
const reducer = ClubSlice.reducer;
const actions = ClubSlice.actions;

const state = (rootState: any): ClubState => rootState[FEATURE_KEY];

const clubSelector = createSelector(state, (state) => {
  return state.club;
});

const customUserAttributesSelector = createSelector(state, (state) => {
  return state.customUserAttributes;
});

const clubTasksSelector = createSelector(state, (state) => {
  return state.tasks;
});

const customUserAttributeTypesSelectSelector = createSelector(
  state,
  (state) => {
    return [
      {
        value: UserCustomAttributeType.DATE,
        label: t('custom_user_attributes.date'),
      },
      {
        value: UserCustomAttributeType.MAIL,
        label: t('custom_user_attributes.email'),
      },
      {
        value: UserCustomAttributeType.MULTI_MAIL,
        label: t('custom_user_attributes.multi_email'),
      },
      {
        value: UserCustomAttributeType.NUMBER,
        label: t('custom_user_attributes.number'),
      },
      {
        value: UserCustomAttributeType.SEX,
        label: t('custom_user_attributes.sex'),
      },
      {
        value: UserCustomAttributeType.STRING as number,
        label: t('custom_user_attributes.text'),
      },
    ] as SelectOption[];
  }
);

const dataProcessingAgreementSelector = createSelector(state, (state) => {
  return state.dataProcessingAgreement;
});

export {
  actions,
  agreeToDataProcessingAgreement,
  clubSelector,
  clubTasksSelector,
  createClubCustomUserAttribute,
  customUserAttributeTypesSelectSelector,
  customUserAttributesSelector,
  dataProcessingAgreementSelector,
  deleteClubCustomUserAttribute,
  fetchClub,
  fetchClubCustomUserAttributes,
  fetchDataProcessingAgreement,
  reducer,
  updateClubCustomUserAttribute,
  updateClubSettings,
  updateClubWelcomeText,
  uploadClubAvatar,
};
